import { useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const STORAGE_KEY = 'sitruna-hub:client-marketplace';

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return {
      clientId: typeof parsed.clientId === 'string' ? parsed.clientId : '',
      marketplace: typeof parsed.marketplace === 'string' ? parsed.marketplace : '',
    };
  } catch {
    return {};
  }
}

function writeStored(clientId, marketplace) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ clientId, marketplace }));
  } catch {
    // ignore quota / privacy-mode errors
  }
}

const MARKETPLACE_OPTIONS = [
  'UK',
  'US',
  'CA',
  'DE',
  'FR',
  'IT',
  'ES',
  'NL',
  'BE',
  'IE',
  'JP',
  'AU',
];

// Persisted client + marketplace state, source-of-truth in the URL query
// string. Lets the user change pages without losing their selection — also
// makes a filter view shareable / bookmarkable.
//
// Marketplace options are the union of `active_channels` (manually maintained
// on the client row, preserves user-specified order) and
// `connected_marketplaces` (derived from `client_connections` — what DataDoe
// actually has data for). active_channels alone misses real Amazon connections
// when the field hasn't been kept up to date.
//
// On every render this validates the persisted marketplace against the merged
// list. If it's no longer valid (e.g. user switched to a client that doesn't
// sell in the persisted region), it falls back to the first valid one and
// rewrites the URL.
//
// Returns:
//   clientId         — current selection (string, '' when none)
//   marketplace      — current selection (string, '' when none)
//   client           — the selected client object (or undefined)
//   marketplaces     — the valid marketplace codes for the selected client
//   setClientId(id)  — update client (auto-resets marketplace to first valid)
//   setMarketplace(m)
export function useClientMarketplaceFilter({ clients = [] } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const clientId = searchParams.get('client') || '';
  const marketplace = searchParams.get('marketplace') || '';

  const client = useMemo(() => clients.find((c) => c.client_id === clientId), [clients, clientId]);

  // Hydrate URL from localStorage when the page loads with no client param.
  // URL stays the source of truth — this just restores last selection across
  // navigation. Stale stored client (removed / no access) gets cleared once
  // the clients list resolves and doesn't contain it.
  useEffect(() => {
    if (clientId) return;
    if (!clients.length) return;
    const stored = readStored();
    if (!stored.clientId) return;
    const exists = clients.some((c) => c.client_id === stored.clientId);
    if (!exists) {
      writeStored('', '');
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set('client', stored.clientId);
    if (stored.marketplace) next.set('marketplace', stored.marketplace);
    setSearchParams(next, { replace: true });
  }, [clientId, clients, searchParams, setSearchParams]);

  // Mirror current selection to localStorage so other pages can pick it up.
  useEffect(() => {
    if (!clientId) return;
    writeStored(clientId, marketplace);
  }, [clientId, marketplace]);

  const marketplaces = useMemo(() => {
    const fromActive = (client?.active_channels || '')
      .split(',')
      .map((s) => s.trim())
      .filter((s) => MARKETPLACE_OPTIONS.includes(s));
    const fromConnections = (client?.connected_marketplaces || []).filter((s) =>
      MARKETPLACE_OPTIONS.includes(s)
    );
    const seen = new Set(fromActive);
    const extras = fromConnections.filter((m) => !seen.has(m));
    return [...fromActive, ...extras];
  }, [client]);

  // Reconcile the persisted marketplace against the client's available
  // channels. Fires on mount, on client change, and whenever the clients list
  // resolves (initial load).
  useEffect(() => {
    if (!clientId) return;
    if (!clients.length) return;
    if (!client) return;
    if (!marketplaces.length) return;
    if (marketplace && marketplaces.includes(marketplace)) return;

    const next = new URLSearchParams(searchParams);
    next.set('marketplace', marketplaces[0]);
    setSearchParams(next, { replace: true });
  }, [clientId, marketplace, marketplaces, client, clients.length]);

  const setClientId = useCallback(
    (id) => {
      const next = new URLSearchParams(searchParams);
      if (id) next.set('client', id);
      else {
        next.delete('client');
        next.delete('marketplace');
        writeStored('', '');
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setMarketplace = useCallback(
    (m) => {
      const next = new URLSearchParams(searchParams);
      if (m) next.set('marketplace', m);
      else next.delete('marketplace');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return { clientId, marketplace, client, marketplaces, setClientId, setMarketplace };
}

// Presentational two-select filter. Pass the values from
// useClientMarketplaceFilter; rendering details vary by `variant`.
//
// variant:
//   'light' (default) — white background, brand-yellow focus ring
//   'dark'            — for dark headers (Copy Studio)
export default function ClientMarketplaceFilter({
  clients,
  clientId,
  marketplace,
  marketplaces,
  onClientChange,
  onMarketplaceChange,
  variant = 'light',
  clientPlaceholder = 'Select a client…',
  showAllMarketplaces = false,
}) {
  const lightSelect =
    'h-7 px-2 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-brand-yellow';
  const darkSelect = 'bg-gray-800 text-white text-xs border border-gray-600 rounded px-2 py-1';
  const selectClass = variant === 'dark' ? darkSelect : lightSelect;

  return (
    <>
      <select
        value={clientId}
        onChange={(e) => onClientChange(e.target.value)}
        className={`${selectClass} ${variant === 'dark' ? 'min-w-[160px]' : ''}`}
      >
        <option value="">{clientPlaceholder}</option>
        {clients.map((c) => (
          <option key={c.client_id} value={c.client_id}>
            {c.brand_name}
          </option>
        ))}
      </select>

      {clientId && (
        <select
          value={marketplace}
          onChange={(e) => onMarketplaceChange(e.target.value)}
          className={selectClass}
        >
          {showAllMarketplaces && <option value="">All marketplaces</option>}
          {marketplaces.length === 0 ? (
            <option value="">No marketplaces</option>
          ) : (
            marketplaces.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))
          )}
        </select>
      )}
    </>
  );
}
